import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGetCurrentUserQuery } from '@/features/users';
import { OfferDetails } from '../../types';
import { getStatusLabel } from '@/helpers';
import { Chip, Icon, Typography } from '@/components';
import { t } from 'i18next';
import { FeedbackSection } from '../drawer/feedback';

type EoiStatusCardProps = {
  readonly offer: OfferDetails;
};

export function EoiStatusCard({ offer }: EoiStatusCardProps) {
  const { data: user } = useGetCurrentUserQuery();
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Madrid',
  };
  const formattedDate: string = offer.createdAt.toLocaleString('es-ES', options);
  const status = getStatusLabel(offer.status.name);
  const availabilitySlots = offer.availability ?? [];
  const statusIcon = status.toLocaleLowerCase() === 'rejected' ? 'circle-x' : 'circle-check';

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>
          <Typography as="h3" weight="semibold" size="title" className="uppercase">
            {t('Expression of Interest Status')}
          </Typography>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="flex items-start gap-3">
            <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon name="check-line" className="text-muted-foreground size-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Typography as="span" size="body-3" weight="normal" className="text-muted-foreground">
                {t('Status')}
              </Typography>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                <Chip color={offer.status.color}>
                  <Icon name={statusIcon} className="h-3 w-3" />
                  {status}
                </Chip>
              </Typography>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon name="calendar" className="text-muted-foreground size-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Typography as="span" size="body-3" weight="normal" className="text-muted-foreground">
                {t('Date Submitted')}
              </Typography>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {formattedDate}
              </Typography>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon name="buildings" className="text-muted-foreground size-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <Typography as="span" size="body-3" weight="normal" className="text-muted-foreground">
                {t('Submitting Entity')}
              </Typography>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {offer.company?.name}
              </Typography>
            </div>
          </div>
        </div>
        {offer.status.name === 'rejected' && <FeedbackSection offer={offer} />}
        {offer.status.name === 'accepted' && offer.availability && offer.availability.length > 0 && (
          <div className="flex items-start gap-3">
            <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon name="clock" className="text-muted-foreground size-4" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Typography as="span" size="body-3" weight="normal" className="text-muted-foreground">
                {t('Availability Slots')}
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                {availabilitySlots.map((slot, index) => (
                  <Chip key={index} color="neutral" className="bg-gray-200 px-3 font-normal">
                    {slot}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
